import { type PointerEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchOutlined } from '@mui/icons-material';
import { createPortal } from 'react-dom';
import { Document, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import LazyPDFPage from './lazy-pdf-page/LazyPDFPage';
import { SelectionState } from './lazy-pdf-page/LazyPDFPage.props';
import { PDFCitationPayload } from './pdf-utils/constants';
import { buildReadingOrderSelection, mergeRects } from './pdf-utils/rect-utils';
import { PDFViewerProps, PDFViewerViewMode } from './PDFViewer.props';
import {
  PdfNativeFrame,
  PdfPageViewerLoader,
  PdfViewerContentWrapper,
  PdfViewerInitialLoadOverlay,
  PdfViewerInitialLoadSpinner,
  PdfViewerResizeOverlay,
  PdfViewerResizeSpinner,
  PdfViewerScrollContainer,
  PdfViewerStateContainer,
  PdfViewerToolbar,
  PdfViewerToolbarActions,
  PdfViewerToolbarButton,
  PdfViewerToolbarDivider,
  PdfViewerToolbarInput,
  PdfViewerToolbarSection,
  PdfViewerToolbarText,
  PdfViewerWrapper,
  StyledCitationNavButton,
  StyledCitationNavigatorWrapper,
  StyledHighlightableTextNotification,
  THUMBNAIL_WIDTH,
} from './PDFViewer.styles';
import { scrollIntoViewUntilSettled } from './scrollIntoViewUntilSettled';
import { Logger } from '../../../utilities';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDF_PAGE_ACTIVE_TOP_OFFSET = 64;
const PDF_PAGE_SCROLL_TOP_OFFSET = 36;
export const PDF_ZOOM_STEP = 0.25;
export const PDF_MIN_ZOOM = 0.75;
export const PDF_MAX_ZOOM = 1.5;
const PDF_DEFERRED_RESIZE_UPDATE_MS = 320;
const PDF_RESIZE_WIDTH_CHANGE_THRESHOLD = 2;
const PDF_RENDER_FALLBACK_MS = 1200;
const PDF_INITIAL_READY_STABILITY_ATTEMPTS = 10;
const PDF_INITIAL_RESIZE_OVERLAY_DELAY_MS = 600;
const PDF_SEARCH_BUTTON_SIZE = 24;
const PDF_SEARCH_ICON_SIZE = 17;
const PDF_SEARCH_BUTTON_STYLE = {
  minWidth: PDF_SEARCH_BUTTON_SIZE,
  padding: 0,
  width: PDF_SEARCH_BUTTON_SIZE,
};
const PDF_SEARCH_ICON_STYLE = {
  fontSize: PDF_SEARCH_ICON_SIZE,
};

interface PdfPanSession {
  pointerId: number;
  startX: number;
  startY: number;
  scrollLeft: number;
  scrollTop: number;
}

const PdfLoader = () => (
  <PdfPageViewerLoader>
    <span>Preparing PDF preview...</span>
  </PdfPageViewerLoader>
);

const PDFViewer = ({
  src,
  base64Content,
  isSourceLoading = false,
  allowNativeTextSelection = false,
  allowPageSelection = false,
  deferResizeUpdates = false,
  pageChrome = 'header',
  selectedPages = new Set(),
  onPageToggle,
  viewMode = PDFViewerViewMode.DOCUMENT,
  zoomScale: controlledZoomScale,
  onZoomScaleChange,
  jsonData,
  activeCitation,
  activeCitationVariant = 'citation',
  activeCitationNonce,
  onCitationPushed,
  searchHighlight,
  searchOpen = false,
  onSearchToggle,
}: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfData, setPdfData] = useState<string | ArrayBuffer | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [activeTokenRefIndex, setActiveTokenRefIndex] = useState(0);
  const [forcedVisiblePage, setForcedVisiblePage] = useState<number | null>(null);
  const [showCitationNotification, setShowCitationNotification] = useState(false);
  const [viewerHostElement, setViewerHostElement] = useState<HTMLDivElement | null>(null);
  const [scrollContainerElement, setScrollContainerElement] = useState<HTMLDivElement | null>(null);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [isPageInputFocused, setIsPageInputFocused] = useState(false);
  const [isFloatingPreviewReady, setIsFloatingPreviewReady] = useState(false);
  const [isResizePreviewPending, setIsResizePreviewPending] = useState(false);
  const [isNativeSelectionCopyFeedbackActive, setIsNativeSelectionCopyFeedbackActive] = useState(false);
  const [uncontrolledZoomScale, setUncontrolledZoomScale] = useState(1);
  const zoomScale = controlledZoomScale ?? uncontrolledZoomScale;
  const [isPanning, setIsPanning] = useState(false);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resizeRenderFallbackRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resizeOverlayEnableTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const resizeFrameRef = useRef<number | null>(null);
  const containerWidthRef = useRef<number | null>(null);
  const pendingContainerWidthRef = useRef<number | null>(null);
  const initialFloatingReadyFrameRef = useRef<number | null>(null);
  const isAwaitingResizeRenderRef = useRef(false);
  const isFloatingResizeOverlayEnabledRef = useRef(false);
  const isFloatingPreviewReadyRef = useRef(false);
  const panSessionRef = useRef<PdfPanSession | null>(null);

  const pdfOwnerDocument = viewerHostElement?.ownerDocument ?? document;
  const pdfOwnerWindow = pdfOwnerDocument.defaultView ?? window;
  const documentOptions = useMemo(() => {
    if (!viewerHostElement?.ownerDocument) {
      return undefined;
    }

    return { ownerDocument: viewerHostElement.ownerDocument };
  }, [viewerHostElement?.ownerDocument]);

  const totalTokenRefs = activeCitation?.tokenReferences.length ?? 0;
  const canCreateCitations = Boolean(onCitationPushed);
  const canUseNativeTextSelection =
    allowNativeTextSelection && !canCreateCitations && viewMode === PDFViewerViewMode.DOCUMENT;

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const updateFloatingPreviewReady = useCallback((nextIsReady: boolean) => {
    isFloatingPreviewReadyRef.current = nextIsReady;
    setIsFloatingPreviewReady(nextIsReady);

    clearTimeout(resizeOverlayEnableTimeoutRef.current);
    if (!nextIsReady) {
      isFloatingResizeOverlayEnabledRef.current = false;
      return;
    }

    resizeOverlayEnableTimeoutRef.current = setTimeout(() => {
      isFloatingResizeOverlayEnabledRef.current = true;
    }, PDF_INITIAL_RESIZE_OVERLAY_DELAY_MS);
  }, []);

  useEffect(() => {
    containerWidthRef.current = containerWidth;
  }, [containerWidth]);

  // Read-only previews use native text selection; show a brief copy-success paint without changing citation mode.
  useEffect(() => {
    clearTimeout(copyFeedbackTimeoutRef.current);
    setIsNativeSelectionCopyFeedbackActive(false);

    if (!canUseNativeTextSelection || !viewerHostElement) {
      return undefined;
    }

    const handleCopy = () => {
      const activeSelection = pdfOwnerWindow.getSelection();
      if (!activeSelection || activeSelection.isCollapsed || activeSelection.rangeCount === 0) {
        return;
      }

      const isSelectionInViewer =
        Boolean(activeSelection.anchorNode && viewerHostElement.contains(activeSelection.anchorNode)) ||
        Boolean(activeSelection.focusNode && viewerHostElement.contains(activeSelection.focusNode));
      if (!isSelectionInViewer) {
        return;
      }

      setIsNativeSelectionCopyFeedbackActive(true);
      clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setIsNativeSelectionCopyFeedbackActive(false);
      }, 900);
    };

    pdfOwnerDocument.addEventListener('copy', handleCopy, true);

    return () => {
      pdfOwnerDocument.removeEventListener('copy', handleCopy, true);
      clearTimeout(copyFeedbackTimeoutRef.current);
      setIsNativeSelectionCopyFeedbackActive(false);
    };
  }, [canUseNativeTextSelection, pdfOwnerDocument, pdfOwnerWindow, viewerHostElement]);

  // The drawer owns the visible scroll container in header mode; register it so
  // zoomed PDFs can use the same pan/scroll helpers as the floating preview.
  useEffect(() => {
    if (pageChrome === 'floating' || !viewerHostElement) {
      return;
    }

    const externalScrollContainer = viewerHostElement.closest<HTMLDivElement>('#pdf-viewer-scroll-container');
    if (!externalScrollContainer) {
      return;
    }

    setScrollContainerElement(externalScrollContainer);
    return () => {
      setScrollContainerElement(current => (current === externalScrollContainer ? null : current));
    };
  }, [pageChrome, viewerHostElement]);

  // ResizeObserver can fire rapidly while the preview pane is dragged; defer
  // expensive page-width changes so react-pdf re-renders once after resizing settles.
  useEffect(() => {
    const measuredElement = pageChrome === 'floating' ? scrollContainerElement : viewerHostElement;
    if (!measuredElement) {
      return;
    }

    const commitMeasuredWidth = (nextWidth: number) => {
      const currentWidth = containerWidthRef.current;
      const shouldDeferResize = deferResizeUpdates && isFloatingPreviewReadyRef.current;

      if (!shouldDeferResize || currentWidth == null) {
        pendingContainerWidthRef.current = null;
        clearTimeout(resizeTimeoutRef.current);
        setContainerWidth(nextWidth);
        return;
      }

      if (Math.abs(nextWidth - currentWidth) < PDF_RESIZE_WIDTH_CHANGE_THRESHOLD) {
        pendingContainerWidthRef.current = null;
        clearTimeout(resizeTimeoutRef.current);
        if (!isAwaitingResizeRenderRef.current) {
          setIsResizePreviewPending(false);
        }
        return;
      }

      if (!isFloatingResizeOverlayEnabledRef.current) {
        pendingContainerWidthRef.current = null;
        clearTimeout(resizeTimeoutRef.current);
        setContainerWidth(nextWidth);
        return;
      }

      pendingContainerWidthRef.current = nextWidth;
      setIsResizePreviewPending(true);
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        const pendingWidth = pendingContainerWidthRef.current;

        pendingContainerWidthRef.current = null;
        const currentCommittedWidth = containerWidthRef.current;
        if (
          pendingWidth == null ||
          (currentCommittedWidth != null &&
            Math.abs(pendingWidth - currentCommittedWidth) < PDF_RESIZE_WIDTH_CHANGE_THRESHOLD)
        ) {
          isAwaitingResizeRenderRef.current = false;
          setIsResizePreviewPending(false);
          return;
        }

        isAwaitingResizeRenderRef.current = true;
        clearTimeout(resizeRenderFallbackRef.current);
        resizeRenderFallbackRef.current = setTimeout(() => {
          isAwaitingResizeRenderRef.current = false;
          setIsResizePreviewPending(false);
        }, PDF_RENDER_FALLBACK_MS);
        setContainerWidth(pendingWidth);
      }, PDF_DEFERRED_RESIZE_UPDATE_MS);
    };
    const measure = () => commitMeasuredWidth(Math.round(measuredElement.getBoundingClientRect().width));
    const ownerWindow = measuredElement.ownerDocument.defaultView ?? window;
    const scheduleMeasure = () => {
      if (resizeFrameRef.current !== null) {
        return;
      }

      resizeFrameRef.current = ownerWindow.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        measure();
      });
    };

    measure();

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(measuredElement);
    return () => {
      observer.disconnect();
      clearTimeout(resizeTimeoutRef.current);
      clearTimeout(resizeRenderFallbackRef.current);
      clearTimeout(resizeOverlayEnableTimeoutRef.current);
      pendingContainerWidthRef.current = null;
      isAwaitingResizeRenderRef.current = false;
      if (resizeFrameRef.current !== null) {
        ownerWindow.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [deferResizeUpdates, pageChrome, scrollContainerElement, viewerHostElement]);

  const calculatedWidth = useMemo(() => {
    if (viewMode === PDFViewerViewMode.THUMBNAIL) {
      return THUMBNAIL_WIDTH;
    }

    const MAX_WIDTH = 1200;
    const VIEWER_BUFFER = 40;
    const CONTAINER_BUFFER = 100;

    const available =
      containerWidth ?? (typeof window !== 'undefined' ? window.innerWidth - CONTAINER_BUFFER : MAX_WIDTH);
    return Math.min(MAX_WIDTH, available - VIEWER_BUFFER);
  }, [viewMode, containerWidth]);

  const renderedWidth = useMemo(() => Math.round(calculatedWidth * zoomScale), [calculatedWidth, zoomScale]);

  // The preview panel may mount before uncached file content arrives; keep the
  // floating viewer shell stable instead of showing a transient missing-source error.
  useEffect(() => {
    const processPdfData = async () => {
      setError(null);

      if (!base64Content && !src) {
        setPdfData(null);
        setLoading(false);
        if (!isSourceLoading) {
          setError('No PDF source provided');
        }
        return;
      }

      setLoading(true);
      try {
        if (base64Content) {
          const binaryString = atob(base64Content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          setPdfData(bytes.buffer);
        } else if (src) {
          setPdfData(src);
        }
      } catch (err) {
        Logger.error('Error processing PDF data:', err);
        setError('Failed to process PDF data');
      } finally {
        setLoading(false);
      }
    };
    processPdfData();
  }, [base64Content, isSourceLoading, src]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setActivePageNumber(1);
    setPageInputValue('1');
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    Logger.error('Error loading PDF:', error);
    setError('Failed to load PDF document');
  }, []);

  // A new PDF source needs a fresh initial-render gate so direct links do not flash a blank viewer.
  useEffect(() => {
    if (initialFloatingReadyFrameRef.current !== null) {
      pdfOwnerWindow.cancelAnimationFrame(initialFloatingReadyFrameRef.current);
      initialFloatingReadyFrameRef.current = null;
    }
    updateFloatingPreviewReady(false);
    setIsResizePreviewPending(false);
    isAwaitingResizeRenderRef.current = false;
    clearTimeout(resizeRenderFallbackRef.current);
  }, [pdfData, pdfOwnerWindow, updateFloatingPreviewReady]);

  const markFloatingPreviewReadyWhenStable = useCallback(() => {
    if (initialFloatingReadyFrameRef.current !== null) {
      return;
    }

    if (!scrollContainerElement) {
      updateFloatingPreviewReady(true);
      return;
    }

    let previousWidth: number | null = null;
    let attempts = 0;
    const checkStableWidth = () => {
      initialFloatingReadyFrameRef.current = null;
      const nextWidth = Math.round(scrollContainerElement.getBoundingClientRect().width);
      const isStable = previousWidth != null && Math.abs(nextWidth - previousWidth) < PDF_RESIZE_WIDTH_CHANGE_THRESHOLD;

      attempts += 1;
      if (isStable || attempts >= PDF_INITIAL_READY_STABILITY_ATTEMPTS) {
        const currentWidth = containerWidthRef.current;
        if (currentWidth == null || Math.abs(nextWidth - currentWidth) >= PDF_RESIZE_WIDTH_CHANGE_THRESHOLD) {
          setContainerWidth(nextWidth);
          previousWidth = null;
          attempts = 0;
          initialFloatingReadyFrameRef.current = pdfOwnerWindow.requestAnimationFrame(checkStableWidth);
          return;
        }

        updateFloatingPreviewReady(true);
        return;
      }

      previousWidth = nextWidth;
      initialFloatingReadyFrameRef.current = pdfOwnerWindow.requestAnimationFrame(checkStableWidth);
    };

    initialFloatingReadyFrameRef.current = pdfOwnerWindow.requestAnimationFrame(checkStableWidth);
  }, [pdfOwnerWindow, scrollContainerElement, updateFloatingPreviewReady]);

  const handlePageRenderSuccess = useCallback(() => {
    if (pageChrome === 'floating') {
      if (!isFloatingPreviewReady) {
        markFloatingPreviewReadyWhenStable();
      }
      if (isAwaitingResizeRenderRef.current) {
        isAwaitingResizeRenderRef.current = false;
        clearTimeout(resizeRenderFallbackRef.current);
        setIsResizePreviewPending(false);
      }
    }
  }, [isFloatingPreviewReady, markFloatingPreviewReadyWhenStable, pageChrome]);

  // Keep the floating page control aligned with the page closest to the top guide while scrolling.
  useEffect(() => {
    if (pageChrome !== 'floating' || !scrollContainerElement || !viewerHostElement || numPages === 0) {
      return;
    }

    let frameId: number | null = null;
    const updateActivePageFromScroll = () => {
      frameId = null;
      const isScrolledToBottom =
        scrollContainerElement.scrollTop + scrollContainerElement.clientHeight >=
        scrollContainerElement.scrollHeight - 2;
      if (isScrolledToBottom) {
        setActivePageNumber(numPages);
        return;
      }

      const pages = Array.from(viewerHostElement.querySelectorAll<HTMLElement>('[data-pdf-page]'));
      const scrollRootRect = scrollContainerElement.getBoundingClientRect();
      const guideTop = scrollRootRect.top + PDF_PAGE_ACTIVE_TOP_OFFSET;
      let nextPageNumber = 1;
      let closestPassedDistance = Number.POSITIVE_INFINITY;
      let firstVisiblePageNumber = 1;
      let firstVisibleDistance = Number.POSITIVE_INFINITY;

      pages.forEach(page => {
        const pageNumber = Number(page.getAttribute('data-pdf-page'));
        if (pageNumber <= 0) {
          return;
        }

        const pageRect = page.getBoundingClientRect();
        if (pageRect.bottom >= guideTop && pageRect.top < firstVisibleDistance) {
          firstVisibleDistance = pageRect.top;
          firstVisiblePageNumber = pageNumber;
        }

        if (pageRect.top <= guideTop) {
          const passedDistance = guideTop - pageRect.top;
          if (passedDistance < closestPassedDistance) {
            closestPassedDistance = passedDistance;
            nextPageNumber = pageNumber;
          }
        }
      });

      if (closestPassedDistance === Number.POSITIVE_INFINITY) {
        nextPageNumber = firstVisiblePageNumber;
      }

      setActivePageNumber(nextPageNumber);
    };
    const scheduleActivePageUpdate = () => {
      if (frameId != null) {
        return;
      }

      frameId = pdfOwnerWindow.requestAnimationFrame(updateActivePageFromScroll);
    };

    scheduleActivePageUpdate();
    scrollContainerElement.addEventListener('scroll', scheduleActivePageUpdate, { passive: true });
    pdfOwnerWindow.addEventListener('resize', scheduleActivePageUpdate);
    return () => {
      scrollContainerElement.removeEventListener('scroll', scheduleActivePageUpdate);
      pdfOwnerWindow.removeEventListener('resize', scheduleActivePageUpdate);
      if (frameId != null) {
        pdfOwnerWindow.cancelAnimationFrame(frameId);
      }
    };
  }, [numPages, pageChrome, pdfOwnerWindow, renderedWidth, scrollContainerElement, viewerHostElement]);

  // Let users type a page number without the scroll listener overwriting the field mid-edit.
  useEffect(() => {
    if (!isPageInputFocused) {
      setPageInputValue(String(activePageNumber));
    }
  }, [activePageNumber, isPageInputFocused]);

  // Reset index and force-render the target page when the active citation changes
  useEffect(() => {
    setActiveTokenRefIndex(0);
  }, [activeCitation?.id]);

  useEffect(() => {
    if (!activeCitation) {
      setForcedVisiblePage(null);
      return;
    }
    const tokenRef = activeCitation.tokenReferences[activeTokenRefIndex];
    if (!tokenRef) {
      return;
    }

    // External API may return page under different field names depending on citation source
    let pageNumber: number | null =
      tokenRef.page ?? (tokenRef as any).page_number ?? (tokenRef as any).pageNumber ?? null;

    // If page is still unknown but we have jsonData, find the page that contains these token_ids
    if (pageNumber === null && jsonData && tokenRef.token_ids?.length > 0) {
      const tokenIdSet = new Set(tokenRef.token_ids);
      const matchingPage = jsonData.find(pd => pd.words.some(w => tokenIdSet.has(w.id)));
      if (matchingPage) {
        pageNumber = matchingPage.page;
      }
    }

    setForcedVisiblePage(pageNumber);

    if (pageNumber === null) {
      return;
    }

    // If jsonData is available, navigate in two phases so a single click reliably
    // takes the user to the source (SCAL-8986). Phase 1: scroll the target page
    // container into view immediately - this both moves the user there and forces
    // the lazy page to mount. Phase 2: once the highlight element renders, land
    // the scroll on it via scrollIntoViewUntilSettled, which keeps re-landing
    // while LATE lazy-page mounts shift the layout (on long documents the pages
    // around the target mount AFTER phase 2 measured, which used to park the
    // first click pages short of the highlight). Phase 1 means navigation never
    // silently fails even if the highlight never resolves (e.g. a page with no
    // OCR rects).
    if (jsonData) {
      let cancelSettle: (() => void) | undefined;
      let safetyTimer: ReturnType<typeof setTimeout> | undefined;
      let observer: MutationObserver | undefined;

      if (pdfOwnerDocument.getElementById('active-citation-highlight')) {
        cancelSettle = scrollIntoViewUntilSettled(pdfOwnerDocument, 'active-citation-highlight');
      } else {
        const pageEl = pdfOwnerDocument.getElementById(`pdf-page-${pageNumber}`);
        if (pageEl) {
          pageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        observer = new MutationObserver(() => {
          const renderedHighlight = pdfOwnerDocument.getElementById('active-citation-highlight');
          if (renderedHighlight) {
            observer?.disconnect();
            clearTimeout(safetyTimer);
            cancelSettle = scrollIntoViewUntilSettled(pdfOwnerDocument, 'active-citation-highlight');
          }
        });
        observer.observe(pdfOwnerDocument.body, { childList: true, subtree: true });
        safetyTimer = setTimeout(() => observer?.disconnect(), 5000);
      }

      return () => {
        observer?.disconnect();
        clearTimeout(safetyTimer);
        cancelSettle?.();
      };
    }

    // No jsonData — scroll to the page element
    const pageEl = pdfOwnerDocument.getElementById(`pdf-page-${pageNumber}`) as HTMLElement | null;
    if (pageEl) {
      pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Page element not yet in DOM — observe until it appears
    let safetyTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      const el = pdfOwnerDocument.getElementById(`pdf-page-${pageNumber}`);
      if (el) {
        observer.disconnect();
        clearTimeout(safetyTimer);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    observer.observe(pdfOwnerDocument.body, { childList: true, subtree: true });
    safetyTimer = setTimeout(() => observer.disconnect(), 3000);
    return () => {
      observer.disconnect();
      clearTimeout(safetyTimer);
    };
    // activeCitationNonce: re-activating the SAME citation (eyeball re-click after
    // scrolling away) bumps the nonce, so this effect re-runs and repeats the jump.
  }, [activeCitation, activeCitationNonce, activeTokenRefIndex, jsonData, pdfOwnerDocument]);

  // Scroll to search highlight within the PDF scroll container only.
  // Two-phase: first bring the page container into view so the lazy page
  // renders, then once the highlight element appears scroll precisely to it.
  useEffect(() => {
    if (!searchHighlight) {
      return;
    }

    setForcedVisiblePage(searchHighlight.pageNumber);

    const scrollContainer =
      viewerHostElement?.closest('#pdf-viewer-scroll-container') ?? viewerHostElement?.parentElement;
    if (!scrollContainer) {
      return;
    }

    const scrollToElement = (el: Element) => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const targetTop = scrollContainer.scrollTop + (elRect.top - containerRect.top) - containerRect.height / 2;
      scrollContainer.scrollTo({ top: targetTop, behavior: 'smooth' });
    };

    // Phase 1: scroll the page container into view so forceVisible + intersection triggers rendering
    const pageEl = pdfOwnerDocument.getElementById(`pdf-page-${searchHighlight.pageNumber}`);
    if (pageEl) {
      scrollToElement(pageEl);
    }

    // Phase 2: wait for the highlight element to appear after the page renders, then scroll precisely
    const scrollToHighlight = () => {
      const el = pdfOwnerDocument.getElementById('search-highlight');
      if (!el) {
        return false;
      }
      // Let the page finish layout before the final scroll
      requestAnimationFrame(() => scrollToElement(el));
      return true;
    };

    if (scrollToHighlight()) {
      return;
    }

    let safetyTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      if (scrollToHighlight()) {
        observer.disconnect();
        clearTimeout(safetyTimer);
      }
    });
    observer.observe(pdfOwnerDocument.body, { childList: true, subtree: true });
    safetyTimer = setTimeout(() => observer.disconnect(), 5000);
    return () => {
      observer.disconnect();
      clearTimeout(safetyTimer);
    };
  }, [searchHighlight, viewerHostElement, pdfOwnerDocument]);

  const handlePrevTokenRef = useCallback(() => {
    setActiveTokenRefIndex(i => Math.max(0, i - 1));
  }, []);

  const handleNextTokenRef = useCallback(() => {
    setActiveTokenRefIndex(i => Math.min((activeCitation?.tokenReferences.length ?? 1) - 1, i + 1));
  }, [activeCitation?.tokenReferences.length]);

  const computeSelection = useCallback((): SelectionState | null => {
    if (!jsonData || viewMode !== PDFViewerViewMode.DOCUMENT) {
      return null;
    }
    const sel = pdfOwnerWindow.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      return null;
    }

    const wordSpanFor = (node: Node | null): HTMLElement | null => {
      const el = node?.nodeType === Node.ELEMENT_NODE ? (node as Element) : (node?.parentElement ?? null);
      return (el?.closest('[data-word-id]') as HTMLElement | null) ?? null;
    };

    const anchorSpan = wordSpanFor(sel.anchorNode);
    if (!anchorSpan) {
      return null;
    }
    const layer = anchorSpan.closest('.custom-text-layer') as HTMLElement | null;
    if (!layer) {
      return null;
    }
    const pageIndex = parseInt(layer.getAttribute('data-page-index') || '0', 10);
    const pageData = jsonData[pageIndex];
    if (!pageData || pageData.words.length === 0) {
      return null;
    }

    const startWordId = parseInt(anchorSpan.dataset.wordId!, 10);

    // Clamp the run to the page where the drag started: if the cursor leaves the
    // page, snap the end to that page's first/last word in the drag direction.
    let endWordId = startWordId;
    const focusSpan = wordSpanFor(sel.focusNode);
    const focusLayer = (focusSpan?.closest('.custom-text-layer') as HTMLElement | null) ?? null;
    if (focusSpan && focusLayer === layer) {
      endWordId = parseInt(focusSpan.dataset.wordId!, 10);
    } else if (focusLayer) {
      const focusPageIndex = parseInt(focusLayer.getAttribute('data-page-index') || '0', 10);
      const edge = focusPageIndex > pageIndex ? pageData.words[pageData.words.length - 1] : pageData.words[0];
      endWordId = edge.id;
    }

    return buildReadingOrderSelection(pageData, pageIndex, startWordId, endWordId);
  }, [jsonData, viewMode, pdfOwnerWindow]);

  // Live drag: the selection run updates continuously while the user drags
  // inside a text layer (throttled to one update per animation frame), and is
  // finalized on mouseup. selectionchange + computeSelection replace the old
  // mouseup-only path so the yellow highlight tracks the drag.
  const draggingRef = useRef(false);
  const selectionRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!canCreateCitations || !jsonData || allowPageSelection || viewMode !== PDFViewerViewMode.DOCUMENT) {
      setSelection(null);
      return undefined;
    }

    const scheduleUpdate = () => {
      if (selectionRafRef.current != null) {
        return;
      }
      selectionRafRef.current = pdfOwnerWindow.requestAnimationFrame(() => {
        selectionRafRef.current = null;
        setSelection(computeSelection());
      });
    };

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      draggingRef.current = Boolean(target?.closest?.('.custom-text-layer'));
    };
    const onSelectionChange = () => {
      if (draggingRef.current) {
        scheduleUpdate();
      }
    };
    const onMouseUp = () => {
      if (!draggingRef.current) {
        return;
      }
      draggingRef.current = false;
      if (selectionRafRef.current != null) {
        pdfOwnerWindow.cancelAnimationFrame(selectionRafRef.current);
        selectionRafRef.current = null;
      }
      setSelection(computeSelection());
    };

    pdfOwnerDocument.addEventListener('mousedown', onMouseDown, true);
    pdfOwnerDocument.addEventListener('selectionchange', onSelectionChange);
    pdfOwnerDocument.addEventListener('mouseup', onMouseUp);
    return () => {
      pdfOwnerDocument.removeEventListener('mousedown', onMouseDown, true);
      pdfOwnerDocument.removeEventListener('selectionchange', onSelectionChange);
      pdfOwnerDocument.removeEventListener('mouseup', onMouseUp);
      if (selectionRafRef.current != null) {
        pdfOwnerWindow.cancelAnimationFrame(selectionRafRef.current);
      }
    };
  }, [allowPageSelection, canCreateCitations, computeSelection, jsonData, pdfOwnerDocument, pdfOwnerWindow, viewMode]);

  const handleCite = useCallback(() => {
    if (!selection || !onCitationPushed) {
      return;
    }
    const mergedRects = mergeRects(selection.rects);
    const payload: PDFCitationPayload = {
      selectedText: selection.text,
      tokenIds: selection.wordIds,
      page: selection.page,
      rects: mergedRects,
    };
    onCitationPushed(payload);
    setSelection(null);
    setShowCitationNotification(true);
    pdfOwnerWindow.getSelection()?.removeAllRanges();
  }, [selection, onCitationPushed, pdfOwnerWindow]);

  // Auto-hide citation notification after 5 seconds
  useEffect(() => {
    if (showCitationNotification) {
      const timer = setTimeout(() => {
        setShowCitationNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showCitationNotification]);

  const citationNotification = showCitationNotification
    ? createPortal(
      <StyledHighlightableTextNotification onClick={() => setShowCitationNotification(false)}>
        <span>✓</span>
        <span>Citation toggled. Now click a cell to link it.</span>
      </StyledHighlightableTextNotification>,
      pdfOwnerDocument.body
    )
    : null;

  const scrollToPage = useCallback(
    (pageNumber: number, behavior: ScrollBehavior = 'smooth') => {
      if (!scrollContainerElement || !viewerHostElement || numPages === 0) {
        return;
      }

      const nextPageNumber = Math.min(Math.max(pageNumber, 1), numPages);
      const pageElement = viewerHostElement.querySelector<HTMLElement>(`[data-pdf-page="${nextPageNumber}"]`);
      if (!pageElement) {
        return;
      }

      const scrollRootRect = scrollContainerElement.getBoundingClientRect();
      const pageRect = pageElement.getBoundingClientRect();
      const nextTop = Math.max(
        0,
        scrollContainerElement.scrollTop + pageRect.top - scrollRootRect.top - PDF_PAGE_SCROLL_TOP_OFFSET
      );
      const previousScrollTop = scrollContainerElement.scrollTop;
      pdfOwnerWindow.requestAnimationFrame(() => {
        scrollContainerElement.scrollTo({ top: nextTop, behavior });
        pdfOwnerWindow.setTimeout(() => {
          const didNotMove = Math.abs(scrollContainerElement.scrollTop - previousScrollTop) < 1;
          const shouldMove = Math.abs(nextTop - previousScrollTop) > 1;
          if (didNotMove && shouldMove) {
            scrollContainerElement.scrollTop = nextTop;
          }
        }, 120);
      });
      setActivePageNumber(nextPageNumber);
    },
    [numPages, pdfOwnerWindow, scrollContainerElement, viewerHostElement]
  );

  const commitPageInput = useCallback(() => {
    const requestedPageNumber = Number(pageInputValue);
    if (!Number.isFinite(requestedPageNumber) || requestedPageNumber <= 0) {
      setPageInputValue(String(activePageNumber));
      return;
    }

    scrollToPage(requestedPageNumber);
  }, [activePageNumber, pageInputValue, scrollToPage]);

  const handlePreviousPreviewPage = useCallback(() => {
    scrollToPage(activePageNumber - 1);
  }, [activePageNumber, scrollToPage]);

  const handleNextPreviewPage = useCallback(() => {
    scrollToPage(activePageNumber + 1);
  }, [activePageNumber, scrollToPage]);

  const showFloatingRenderOverlay = useCallback(() => {
    if (pageChrome !== 'floating' || !isFloatingPreviewReady) {
      return;
    }

    isAwaitingResizeRenderRef.current = true;
    setIsResizePreviewPending(true);
    clearTimeout(resizeRenderFallbackRef.current);
    resizeRenderFallbackRef.current = setTimeout(() => {
      isAwaitingResizeRenderRef.current = false;
      setIsResizePreviewPending(false);
    }, PDF_RENDER_FALLBACK_MS);
  }, [isFloatingPreviewReady, pageChrome]);

  const handleZoomScaleChange = useCallback(
    (nextZoomScale: number) => {
      showFloatingRenderOverlay();
      onZoomScaleChange?.(nextZoomScale);

      if (controlledZoomScale == null) {
        setUncontrolledZoomScale(nextZoomScale);
      }
    },
    [controlledZoomScale, onZoomScaleChange, showFloatingRenderOverlay]
  );

  const handleZoomOut = useCallback(() => {
    const nextZoomScale = Math.max(PDF_MIN_ZOOM, zoomScale - PDF_ZOOM_STEP);
    if (nextZoomScale === zoomScale) {
      return;
    }

    handleZoomScaleChange(nextZoomScale);
  }, [handleZoomScaleChange, zoomScale]);

  const handleZoomIn = useCallback(() => {
    const nextZoomScale = Math.min(PDF_MAX_ZOOM, zoomScale + PDF_ZOOM_STEP);
    if (nextZoomScale === zoomScale) {
      return;
    }

    handleZoomScaleChange(nextZoomScale);
  }, [handleZoomScaleChange, zoomScale]);

  const handleFitWidth = useCallback(() => {
    if (zoomScale === 1) {
      return;
    }

    handleZoomScaleChange(1);
  }, [handleZoomScaleChange, zoomScale]);

  const isPdfPannable = viewMode === PDFViewerViewMode.DOCUMENT && zoomScale > 1 && Boolean(scrollContainerElement);

  const stopPanning = useCallback((event?: PointerEvent<HTMLDivElement>) => {
    const panSession = panSessionRef.current;
    if (!panSession) {
      return;
    }

    if (event?.currentTarget.hasPointerCapture(panSession.pointerId)) {
      event.currentTarget.releasePointerCapture(panSession.pointerId);
    }

    panSessionRef.current = null;
    setIsPanning(false);
  }, []);

  const handlePdfPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!isPdfPannable || event.button !== 0 || !scrollContainerElement) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const isInteractiveTarget = Boolean(target?.closest('button, input, textarea, select, a, [role="button"]'));
      const isTextSelectionTarget = Boolean(
        target?.closest('.textLayer span, .textLayer br, .custom-text-layer [data-word-id]')
      );
      if (isInteractiveTarget || isTextSelectionTarget) {
        return;
      }

      panSessionRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: scrollContainerElement.scrollLeft,
        scrollTop: scrollContainerElement.scrollTop,
      };
      setIsPanning(true);
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [isPdfPannable, scrollContainerElement]
  );

  const handlePdfPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const panSession = panSessionRef.current;
      if (!panSession || !scrollContainerElement) {
        return;
      }

      scrollContainerElement.scrollLeft = panSession.scrollLeft - (event.clientX - panSession.startX);
      scrollContainerElement.scrollTop = panSession.scrollTop - (event.clientY - panSession.startY);
      event.preventDefault();
    },
    [scrollContainerElement]
  );

  const handlePdfPointerEnd = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (panSessionRef.current?.pointerId !== event.pointerId) {
        return;
      }

      stopPanning(event);
    },
    [stopPanning]
  );

  // If zoom returns to fit-width while dragging, end the pan session so the
  // cursor and captured pointer do not stay stuck in panning mode.
  useEffect(() => {
    if (!isPdfPannable) {
      stopPanning();
    }
  }, [isPdfPannable, stopPanning]);

  const isFloatingChrome = pageChrome === 'floating';
  const hasPdfSource = Boolean(base64Content || src);
  const isWaitingForPdfSource = loading || isSourceLoading || !pdfData;

  if (loading && !isFloatingChrome) {
    return (
      <PdfViewerWrapper>
        <PdfLoader />
      </PdfViewerWrapper>
    );
  }

  if (error && src && !isFloatingChrome) {
    return (
      <PdfViewerWrapper ref={setViewerHostElement} $viewMode={viewMode} $pageChrome={pageChrome}>
        <PdfNativeFrame src={src} title="PDF preview" />
      </PdfViewerWrapper>
    );
  }

  if (error) {
    return <PdfViewerStateContainer style={{ color: 'red' }}>Error: {error}</PdfViewerStateContainer>;
  }

  if (!pdfData && !isSourceLoading && !hasPdfSource) {
    return <PdfViewerStateContainer>No PDF available</PdfViewerStateContainer>;
  }

  const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);
  const isWaitingForFloatingLayout = isFloatingChrome && containerWidth == null;
  const isFloatingPreviewLoading =
    isFloatingChrome && (isWaitingForPdfSource || isWaitingForFloatingLayout || !isFloatingPreviewReady);
  const isFloatingPreviewResizing = isFloatingChrome && isResizePreviewPending && !isFloatingPreviewLoading;
  const isFloatingToolbarMounted = isFloatingChrome;
  const isFloatingToolbarVisible =
    isFloatingToolbarMounted && numPages > 0 && isFloatingPreviewReady && !isWaitingForPdfSource;
  const zoomPercentage = Math.round(zoomScale * 100);
  let pdfDocumentContent: ReactNode = null;

  if (documentOptions && pdfData && !isWaitingForFloatingLayout) {
    pdfDocumentContent = (
      <Document
        file={pdfData}
        options={documentOptions}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={<></>}>
        <PdfViewerContentWrapper
          $isHidden={isFloatingPreviewLoading}
          $isResizing={isFloatingPreviewResizing}
          $thumbnailWidth={viewMode === PDFViewerViewMode.THUMBNAIL ? renderedWidth : undefined}
          $viewMode={viewMode}
          $pageChrome={pageChrome}>
          {pageNumbers.map((pageNum, index) => {
            const pageData = jsonData?.find(p => p.page === pageNum);
            return (
              <LazyPDFPage
                key={pageNum}
                pageNumber={pageNum}
                pageIndex={index}
                width={renderedWidth}
                totalPages={numPages}
                allowPageSelection={allowPageSelection}
                allowNativeTextSelection={allowNativeTextSelection}
                isSelected={selectedPages.has(pageNum)}
                onToggle={onPageToggle}
                onRenderSuccess={handlePageRenderSuccess}
                pageChrome={pageChrome}
                viewMode={viewMode}
                pageData={pageData}
                nativeSelectionCopyFeedbackActive={isNativeSelectionCopyFeedbackActive}
                activeCitation={activeCitation}
                activeCitationVariant={activeCitationVariant}
                activeTokenRefIndex={activeTokenRefIndex}
                forceVisible={pageNum === forcedVisiblePage || pageNum === searchHighlight?.pageNumber}
                selection={selection}
                onCite={handleCite}
                citationEnabled={canCreateCitations}
                searchHighlight={searchHighlight}
              />
            );
          })}
        </PdfViewerContentWrapper>
      </Document>
    );
  } else if (!isFloatingChrome) {
    pdfDocumentContent = <PdfLoader />;
  }

  return (
    <PdfViewerWrapper
      ref={setViewerHostElement}
      $viewMode={viewMode}
      $pageChrome={pageChrome}
      $isPannable={pageChrome !== 'floating' && isPdfPannable}
      $isPanning={pageChrome !== 'floating' && isPanning}
      $nativeSelectionCopyFeedbackActive={isNativeSelectionCopyFeedbackActive}
      $nativeSelectionEnabled={canUseNativeTextSelection}
      data-pannable={pageChrome !== 'floating' && isPdfPannable ? 'true' : undefined}
      data-panning={pageChrome !== 'floating' && isPanning ? 'true' : undefined}
      onPointerCancel={pageChrome !== 'floating' ? handlePdfPointerEnd : undefined}
      onPointerDown={pageChrome !== 'floating' ? handlePdfPointerDown : undefined}
      onPointerMove={pageChrome !== 'floating' ? handlePdfPointerMove : undefined}
      onPointerUp={pageChrome !== 'floating' ? handlePdfPointerEnd : undefined}>
      {isFloatingToolbarMounted && (
        <PdfViewerToolbar
          aria-hidden={!isFloatingToolbarVisible}
          aria-label="PDF preview controls"
          style={{ visibility: isFloatingToolbarVisible ? 'visible' : 'hidden' }}>
          <PdfViewerToolbarSection>
            <PdfViewerToolbarButton
              aria-label="Previous page"
              disabled={activePageNumber <= 1}
              onClick={handlePreviousPreviewPage}
              type="button">
              ‹
            </PdfViewerToolbarButton>
            <PdfViewerToolbarInput
              aria-label="PDF page number"
              inputMode="numeric"
              max={numPages}
              min={1}
              onBlur={() => {
                commitPageInput();
                setIsPageInputFocused(false);
              }}
              onChange={event => setPageInputValue(event.target.value.replace(/\D/g, ''))}
              onFocus={() => setIsPageInputFocused(true)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.currentTarget.blur();
                }
              }}
              type="text"
              value={pageInputValue}
            />
            <PdfViewerToolbarText>/ {numPages}</PdfViewerToolbarText>
            <PdfViewerToolbarButton
              aria-label="Next page"
              disabled={activePageNumber >= numPages}
              onClick={handleNextPreviewPage}
              type="button">
              ›
            </PdfViewerToolbarButton>
          </PdfViewerToolbarSection>

          <PdfViewerToolbarActions>
            {onSearchToggle ? (
              <PdfViewerToolbarSection>
                <PdfViewerToolbarButton
                  aria-label={searchOpen ? 'Close PDF search' : 'Search in PDF'}
                  aria-pressed={searchOpen}
                  onClick={onSearchToggle}
                  style={PDF_SEARCH_BUTTON_STYLE}
                  type="button">
                  <SearchOutlined style={PDF_SEARCH_ICON_STYLE} />
                </PdfViewerToolbarButton>
              </PdfViewerToolbarSection>
            ) : null}

            <PdfViewerToolbarSection>
              <PdfViewerToolbarButton
                aria-label="Zoom out"
                disabled={zoomScale <= PDF_MIN_ZOOM}
                onClick={handleZoomOut}
                type="button">
                −
              </PdfViewerToolbarButton>
              <PdfViewerToolbarText>{zoomPercentage}%</PdfViewerToolbarText>
              <PdfViewerToolbarButton
                aria-label="Zoom in"
                disabled={zoomScale >= PDF_MAX_ZOOM}
                onClick={handleZoomIn}
                type="button">
                +
              </PdfViewerToolbarButton>
              <PdfViewerToolbarDivider aria-hidden="true" />
              <PdfViewerToolbarButton
                aria-label="Fit PDF to preview width"
                disabled={zoomScale === 1}
                onClick={handleFitWidth}
                type="button">
                Fit
              </PdfViewerToolbarButton>
            </PdfViewerToolbarSection>
          </PdfViewerToolbarActions>
        </PdfViewerToolbar>
      )}
      {pageChrome === 'floating' ? (
        <>
          <PdfViewerScrollContainer
            ref={setScrollContainerElement}
            data-panning={isPanning ? 'true' : undefined}
            $isPannable={isPdfPannable}
            $isPanning={isPanning}
            $isResizing={isFloatingPreviewResizing}
            onPointerCancel={handlePdfPointerEnd}
            onPointerDown={handlePdfPointerDown}
            onPointerMove={handlePdfPointerMove}
            onPointerUp={handlePdfPointerEnd}>
            {pdfDocumentContent}
            {isFloatingPreviewLoading && (
              <PdfViewerInitialLoadOverlay aria-busy="true" aria-live="polite">
                <PdfViewerInitialLoadSpinner size={28} thickness={4} />
                <span>Preparing PDF preview...</span>
              </PdfViewerInitialLoadOverlay>
            )}
          </PdfViewerScrollContainer>
          {isFloatingPreviewResizing && (
            <PdfViewerResizeOverlay aria-busy="true" aria-live="polite">
              <PdfViewerResizeSpinner size={24} thickness={4} />
              <span>Resizing preview...</span>
            </PdfViewerResizeOverlay>
          )}
        </>
      ) : (
        pdfDocumentContent
      )}

      {totalTokenRefs > 1 &&
        createPortal(
          <StyledCitationNavigatorWrapper>
            <StyledCitationNavButton
              onClick={handlePrevTokenRef}
              disabled={activeTokenRefIndex === 0}
              size="small"
              variant="outlined"
              title="Previous citation">
              ↑
            </StyledCitationNavButton>
            <span>
              {activeTokenRefIndex + 1} / {totalTokenRefs}
            </span>
            <StyledCitationNavButton
              onClick={handleNextTokenRef}
              disabled={activeTokenRefIndex === totalTokenRefs - 1}
              size="small"
              variant="outlined"
              title="Next citation">
              ↓
            </StyledCitationNavButton>
          </StyledCitationNavigatorWrapper>,
          pdfOwnerDocument.body
        )}
      {citationNotification}
    </PdfViewerWrapper>
  );
};

export default PDFViewer;
